import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Facebook, MessageSquare } from "lucide-react";

export const ContactSection = ({ onGetQuoteClick }) => {
  const contactInfo = [
  {
    icon: Phone,
    label: "Phone",
    value: "07803 602247",
    href: "tel:07803602247",
    description: "Mon-Fri, 8am-6pm"
  },
  {
    icon: Mail,
    label: "Email",
    value: "wantrobus@hotmail.co.uk",
    href: "mailto:wantrobus@hotmail.co.uk",
    description: "We'll respond within 24hrs"
  },
  {
    icon: MapPin,
    label: "Location",
    value: "Southport, Merseyside",
    href: null,
    description: "Covering the North West"
  }];


  const serviceAreas = [
  "Southport",
  "Formby",
  "Crosby",
  "Bootle",
  "Liverpool",
  "Ormskirk",
  "Penwortham",
  "Preston",
  "Lancashire",
  "Merseyside"];


  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-slate-900 text-white">

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Column - Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}>

            <span className="tech-label text-royal-light mb-4 block">READY TO BUILD?</span>
            <h2
              data-testid="contact-heading"
              className="font-heading text-4xl md:text-5xl font-semibold tracking-tight mb-6">

              Get In Touch
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed mb-12">
              Ready to start your building or joinery project? Contact us for a 
              free, no-obligation quote. We're always happy to discuss your ideas.
            </p>

            {/* Contact Cards */}
            <div className="space-y-6 mb-12">
              {contactInfo.map((info, index) =>
              <motion.div
                key={info.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`contact-info-${info.label.toLowerCase()}`}
                className="flex items-start gap-4 p-6 bg-slate-800/50 border border-slate-700 hover:border-royal/50 transition-colors">

                  <div className="w-12 h-12 flex items-center justify-center bg-slate-800 border border-slate-700">
                    <info.icon size={20} className="text-royal-light" />
                  </div>
                  <div>
                    <div className="font-mono text-xs text-slate-500 uppercase tracking-wider mb-1">
                      {info.label}
                    </div>
                    {info.href ?
                  <a
                    href={info.href}
                    className="font-heading text-xl font-semibold text-white hover:text-royal-light transition-colors">

                        {info.value}
                      </a> :

                  <div className="font-heading text-xl font-semibold text-white">
                        {info.value}
                      </div>
                  }
                    <div className="text-sm text-slate-500 mt-1">
                      {info.description}
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Social Link */}
            <a
              href="https://www.facebook.com/jantrobusandson"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-facebook"
              className="inline-flex items-center gap-3 text-slate-400 hover:text-white transition-colors">

              <Facebook size={20} />
              <span className="font-medium">Follow us on Facebook</span>
            </a>
          </motion.div>

          {/* Right Column - Areas & Hours */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}>

            {/* Areas We Cover */}
            <div className="mb-12">
              <h3 className="font-heading text-2xl font-semibold mb-6 flex items-center gap-3">
                <MapPin size={24} className="text-royal-light" />
                Areas We Cover
              </h3>
              <div className="flex flex-wrap gap-3">
                {serviceAreas.map((area, index) =>
                <span
                  key={area}
                  data-testid={`service-area-${index}`}
                  className="px-4 py-2 bg-slate-800 border border-slate-700 text-sm text-slate-300 font-mono">

                    {area}
                  </span>
                )}
              </div>
            </div>

            {/* Business Hours */}
            <div className="p-8 bg-slate-800/50 border border-slate-700">
              <h3 className="font-heading text-xl font-semibold mb-6 flex items-center gap-3">
                <Clock size={20} className="text-royal-light" />
                Business Hours
              </h3>
              <div className="space-y-3 font-mono text-sm">
                <div className="flex justify-between text-slate-300">
                  <span>Monday - Friday</span>
                  <span className="text-white">8:00 AM - 6:00 PM</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Saturday</span>
                  <span className="text-white">9:00 AM - 1:00 PM</span>
                </div>
                <div className="flex justify-between text-slate-300">
                  <span>Sunday</span>
                  <span className="text-slate-500">Closed</span>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-slate-700">
                <p className="text-sm text-slate-400">
                  Emergency call-outs available for existing customers.
                </p>
              </div>
            </div>

            {/* CTA Card */}
            <div className="mt-8 p-8 bg-royal text-white">
              <h3 className="font-heading text-2xl font-semibold mb-3">
                Ready to Start?
              </h3>
              <p className="text-white/80 mb-6">
                Contact us today for a free quote on your building or joinery project.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="tel:07803602247"
                  data-testid="contact-cta-phone"
                  className="inline-flex items-center gap-2 bg-white text-slate-900 px-6 py-3 font-heading font-semibold text-sm uppercase tracking-wide hover:bg-slate-100 transition-colors">

                  <Phone size={18} />
                  Call Now
                </a>
                <button
                  onClick={onGetQuoteClick}
                  data-testid="contact-cta-form"
                  className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-6 py-3 font-heading font-semibold text-sm uppercase tracking-wide hover:bg-white hover:text-slate-900 transition-colors">

                  <MessageSquare size={18} />
                  Request Quote
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>);

};